<template>
    <div class="screen-control-page">
        <!-- 顶部搜索 -->
        <div class="search-bar">
            <van-search
                v-model="filterForm.screenName"
                placeholder="输入媒体名称搜索"
                shape="round"
                @search="handleSearch"
                @clear="handleSearch"
            />
        </div>

        <!-- 地区筛选 -->
        <div class="filter-panel">
            <div class="filter-row three-col">
                <div class="filter-cell">
                    <el-select v-model="filterForm.province" placeholder="省份" size="mini" clearable class="full-select" @change="onProvinceChange">
                        <el-option v-for="item in regionData" :key="item.name" :label="item.name" :value="item.name" />
                    </el-select>
                </div>
                <div class="filter-cell">
                    <el-select v-model="filterForm.city" placeholder="城市" size="mini" clearable class="full-select" @change="onCityChange" :disabled="!filterForm.province">
                        <el-option v-for="item in cityOptions" :key="item.name" :label="item.name" :value="item.name" />
                    </el-select>
                </div>
                <div class="filter-cell">
                    <el-select v-model="filterForm.district" placeholder="区县" size="mini" clearable class="full-select" :disabled="!filterForm.city" @change="handleSearch">
                        <el-option v-for="item in districtOptions" :key="item" :label="item" :value="item" />
                    </el-select>
                </div>
            </div>
        </div>

        <!-- 全选 + 操作按钮 -->
        <div class="action-bar" v-if="showScreenControl()">
            <van-checkbox v-model="isAllSelected" @change="toggleSelectAll">全选</van-checkbox>
            <div class="action-btns">
                <van-button type="info" size="small" :disabled="!hasSelection" @click="switchPlate('ON')">一键开屏</van-button>
                <van-button type="danger" size="small" :disabled="!hasSelection" @click="switchPlate('OFF')">一键关屏</van-button>
            </div>
        </div>

        <!-- 列表 -->
        <div class="list-container" ref="listContainer" @scroll="handleScroll">
            <div class="screen-list">
                <div v-if="loading && screenList.length === 0" class="loading-tip">
                    <van-loading size="24px">数据加载中...</van-loading>
                </div>
                <div v-else-if="screenList.length === 0" class="empty-tip">
                    <van-icon name="info-o" size="24" />
                    <span>暂无数据</span>
                </div>
                <div v-else v-for="(item, index) in screenList" :key="item.screenId" class="screen-item">
                    <div class="item-photo" @click="showScreenControl() ? toggleItem(item) : goToMediaDetail(item)">
                        <img v-if="getPhotoUrl(item)" :src="getPhotoUrl(item)" />
                        <div v-else class="photo-placeholder">
                            <van-icon name="photo-o" size="24" color="#ccc" />
                        </div>
                        <div v-if="showScreenControl()" class="item-check" @click.stop="toggleItem(item)">
                            <van-checkbox :value="item._selected" />
                        </div>
                    </div>
                    <div class="item-body" @click="goToMediaDetail(item)">
                        <div class="item-header">
                            <span class="screen-name">{{ item.screenName }}</span>
                            <van-tag v-if="parseScene(item.sceneModelArea)" type="primary" size="mini">{{ parseScene(item.sceneModelArea) }}</van-tag>
                        </div>
                        <div class="item-info">
                            <span class="info-tag">{{ item.turnTime || '--' }}</span>
                            <span class="info-divider">|</span>
                            <span class="info-tag">{{ item.resolution || '--' }}</span>
                        </div>
                        <div class="item-addr">
                            <van-icon name="location-o" size="12" />
                            <span>{{ item.city }}{{ item.district }}{{ item.address || '' }}</span>
                        </div>
                        <div v-if="item.userName || item.userCompanyName" class="item-owner">
                            <van-icon name="contact" size="12" />
                            <span>{{ item.userName || '' }}{{ item.userCompanyName ? ' · ' + item.userCompanyName : '' }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- 触底加载提示 -->
            <div v-if="loadingMore" class="loading-more-tip">
                <van-loading size="20px">加载更多...</van-loading>
            </div>
            <div v-if="noMoreData && screenList.length > 0" class="no-more-tip">— 已加载全部数据 —</div>
        </div>

        <!-- 监播悬浮按钮 -->
        <div class="float-monitor-btn" @click="handleMonitorClick">
            <van-icon name="video-o" size="24" color="#fff" />
        </div>

        <!-- 短信验证弹窗 -->
        <van-dialog
            v-model="smsDialogVisible"
            title="手机短信验证"
            show-cancel-button
            :before-close="handleSmsConfirm"
        >
            <div class="sms-dialog-body">
                <p v-if="switchValue === 'OFF'" class="sms-warning">本操作会导致户外大屏立即黑屏，请谨慎操作！</p>
                <p class="sms-tip">{{ smsCount > 0 ? `已向 ${phone} 发送验证码` : `向 ${phone} 发送验证码` }}</p>
                <div class="sms-input-row">
                    <van-field v-model="smsCode" type="digit" maxlength="6" placeholder="请输入验证码" />
                    <van-button
                        size="small"
                        type="info"
                        :disabled="smsCount > 0"
                        @click="sendSmsCode"
                    >{{ smsCount > 0 ? `${smsCount}秒后重获取` : '获取验证码' }}</van-button>
                </div>
            </div>
        </van-dialog>
    </div>
</template>

<script>
import { SCREEN_CONTROL_CODES } from '@/utils/permission';
import { regionData, toBackendDistrict, toDisplayDistrict } from '@/utils/regionData';

export default {
    name: 'ScreenControlView',
    data() {
        return {
            regionData,
            screenList: [],
            filterForm: {
                pageNum: 0,
                pageSize: 20,
                screenName: '',
                province: '',
                city: '',
                district: '',
                superviseLevelList: [1, 2],
            },
            total: 0,
            loading: false,
            loadingMore: false,
            noMoreData: false,
            isAllSelected: false,
            smsDialogVisible: false,
            smsCode: '',
            smsCount: 0,
            smsTimer: null,
            switchOffParams: null,
            switchValue: '',
            phone: '',
            savedScrollTop: 0,
        };
    },
    computed: {
        hasSelection() {
            return this.screenList.some(item => item._selected);
        },
        cityOptions() {
            if (!this.filterForm.province) return [];
            const province = this.regionData.find(r => r.name === this.filterForm.province);
            return province ? province.city : [];
        },
        districtOptions() {
            if (!this.filterForm.province || !this.filterForm.city) return [];
            const province = this.regionData.find(r => r.name === this.filterForm.province);
            if (!province) return [];
            const city = province.city.find(c => c.name === this.filterForm.city);
            return city ? city.area || [] : [];
        },
    },
    mounted() {
        const userInfo = JSON.parse(window.sessionStorage.getItem('userInfo') || '{}');
        this.phone = userInfo.phone ? userInfo.phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '';
        this.queryPageList();
    },
    activated() {
        if (this.$refs.listContainer && this.savedScrollTop > 0) {
            this.$refs.listContainer.scrollTop = this.savedScrollTop;
        }
    },
    beforeRouteLeave(to, from, next) {
        if (this.$refs.listContainer) {
            this.savedScrollTop = this.$refs.listContainer.scrollTop;
        }
        next();
    },
    beforeDestroy() {
        if (this.smsTimer) clearInterval(this.smsTimer);
    },
    methods: {
        showScreenControl() {
            const userCode = window.sessionStorage.getItem('userCode') || '';
            return SCREEN_CONTROL_CODES.includes(userCode);
        },
        onProvinceChange() {
            this.filterForm.city = '';
            this.filterForm.district = '';
        },
        onCityChange() {
            this.filterForm.district = '';
        },
        handleMonitorClick() {
            this.$dialog.confirm({
                title: '温馨提示',
                message: '监播涉及多媒体（图片/视频）播放，可能消耗较多移动数据流量。加载速度取决于网络状况，请耐心等待加载。',
            }).then(() => {
                this.$router.push('/home');
            }).catch(() => {});
        },
        goToMediaDetail(item) {
            this.$router.push({
                path: '/mediaDetail',
                query: {
                    screenId: item.screenId || '',
                    screenName: item.screenName || '',
                    coverThumbnailUrl: item.coverThumbnailUrl || '',
                    realPictureUrl: item.realPictureUrl || '',
                    resolution: item.resolution || '',
                    turnTime: item.turnTime || '',
                    province: item.province || '',
                    city: item.city || '',
                    district: item.district || '',
                    address: item.address || '',
                    sceneModelArea: item.sceneModelArea || '',
                    userCompanyName: item.userCompanyName || '',
                    userName: item.userName || '',
                    userPhone: item.userPhone || '',
                    longitude: item.longitude || '',
                    latitude: item.latitude || '',
                },
            });
        },
        handleSearch() {
            this.screenList = [];
            this.filterForm.pageNum = 0;
            this.noMoreData = false;
            this.isAllSelected = false;
            this.queryPageList();
        },
        toggleSelectAll(val) {
            this.screenList.forEach(item => this.$set(item, '_selected', val));
        },
        toggleItem(item) {
            this.$set(item, '_selected', !item._selected);
            this.isAllSelected = this.screenList.every(i => i._selected);
        },
        getSwitchText(val) {
            if (val === null || val === 3) return '不在线';
            if (val === 2) return '开屏';
            if (val === 1) return '关屏';
            return '未知';
        },
        getSwitchTagType(val) {
            if (val === null || val === 3) return 'default';
            if (val === 2) return 'success';
            if (val === 1) return 'danger';
            return 'default';
        },
        getPhotoUrl(item) {
            if (item.coverThumbnailUrl) return item.coverThumbnailUrl;
            if (item.realPictureUrl) {
                const { resourceUrl } = require('@/utils/base');
                const first = item.realPictureUrl.split(',')[0];
                return first ? resourceUrl + first : '';
            }
            return '';
        },
        parseScene(val) {
            if (!val) return '';
            const parts = val.split('/');
            return parts[0] || '';
        },
        async queryPageList() {
            this.loading = true;
            const params = { ...this.filterForm };
            params.district = toBackendDistrict(params.district);

            try {
                const res = await this.$post('/monitor/monitorscreen/queryPageList', params);
                if (res.rescode === 200) {
                    const list = (res.data.dataList || []).map(item => ({
                        ...item,
                        district: toDisplayDistrict(item.district),
                        _selected: this.isAllSelected,
                    }));
                    if (this.filterForm.pageNum === 0) {
                        this.screenList = list;
                    } else {
                        this.screenList = [...this.screenList, ...list];
                    }
                    this.total = res.data.totalCount || 0;
                    this.noMoreData = this.screenList.length >= this.total;
                } else {
                    this.$toast(res.msg);
                }
            } catch (error) {
                console.error(error);
                this.$toast('数据加载失败');
            } finally {
                this.loading = false;
                this.loadingMore = false;
            }
        },
        handleScroll() {
            if (this.loadingMore || this.noMoreData || this.loading) return;
            const c = this.$refs.listContainer;
            if (!c) return;
            if (c.scrollTop + c.clientHeight >= c.scrollHeight - 20) {
                this.filterForm.pageNum += 1;
                this.loadingMore = true;
                this.queryPageList();
            }
        },
        switchPlate(value) {
            this.switchValue = value;
            const selectedList = this.screenList.filter(item => item._selected);
            if (selectedList.length === 0) {
                this.$toast('请先选择媒体');
                return;
            }

            const screenIdList = selectedList.map(item => item.screenId);
            const params = {
                screenIdList,
                value,
                operationType: [0],
            };

            this.$dialog.confirm({
                title: '注意',
                message: `确定${value === 'ON' ? '开屏' : '关屏'}${screenIdList.length}块屏吗？`,
            }).then(() => {
                if (value === 'ON') {
                    // 开屏：直接执行，不需要验证码
                    this.doSwitchPlate(params);
                } else {
                    // 关屏：需要短信验证码
                    this.switchOffParams = params;
                    this.smsCode = '';
                    this.smsDialogVisible = true;
                }
            }).catch(() => {});
        },
        async doSwitchPlate(params) {
            try {
                const res = await this.$post('/monitor/monitorscreen/controlOnOffScreen', params);
                if (res.rescode === 200) {
                    this.$toast.success('操作成功');
                    this.handleSearch();
                    return true;
                } else {
                    this.$toast(res.msg || '操作失败');
                    return false;
                }
            } catch (error) {
                console.error(error);
                this.$toast('操作失败');
                return false;
            }
        },
        async sendSmsCode() {
            try {
                const res = await this.$get('/smsSendCode', { phone: '' });
                if (res.rescode === 200) {
                    this.$toast.success('验证码已发送');
                    this.smsCount = 59;
                    this.smsTimer = setInterval(() => {
                        this.smsCount -= 1;
                        if (this.smsCount <= 0) clearInterval(this.smsTimer);
                    }, 1000);
                } else {
                    this.$toast(res.msg || '发送失败');
                }
            } catch (error) {
                this.$toast('发送短信频繁，请稍后重试');
            }
        },
        handleSmsConfirm(action, done) {
            if (action === 'confirm') {
                const code = this.smsCode.trim();
                if (!code || code.length < 6) {
                    this.$toast('请输入6位验证码');
                    done(false);
                    return;
                }
                const params = { ...this.switchOffParams, code };
                this.doSwitchPlate(params).then(success => {
                    if (success) {
                        this.switchOffParams = null;
                        this.smsDialogVisible = false;
                        done();
                    } else {
                        done(false);
                    }
                });
            } else {
                this.switchOffParams = null;
                done();
            }
        },
    },
};
</script>

<style scoped>
.screen-control-page {
    height: 100vh;
    background: #f5f7fa;
    display: flex;
    flex-direction: column;
}

/* 搜索栏 */
.search-bar {
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.search-bar ::v-deep .van-search {
    padding: 8px 12px;
}

/* 筛选面板 */
.filter-panel {
    background: #fff;
    padding: 6px 12px 10px;
    border-bottom: 1px solid #f0f0f0;
}
.filter-row {
    display: flex;
    gap: 10px;
}
.filter-row.three-col .filter-cell {
    flex: 1;
    min-width: 0;
}
.filter-cell {
    margin-bottom: 6px;
}
.filter-label {
    display: block;
    font-size: 12px;
    color: #969799;
    margin-bottom: 4px;
}
.full-select {
    width: 100%;
}

.action-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #fff;
    border-bottom: 1px solid #f0f0f0;
}

.action-btns {
    display: flex;
    gap: 8px;
}

.list-container {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 12px;
    padding-bottom: 72px;
}

.screen-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.loading-tip, .empty-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 0;
    gap: 8px;
    color: #969799;
    font-size: 14px;
}

.loading-more-tip,
.no-more-tip {
    text-align: center;
    padding: 18px 0;
    color: #c8c9cc;
    font-size: 12px;
}

.screen-item {
    display: flex;
    background: #fff;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    overflow: hidden;
    height: 100px;
}

.item-photo {
    width: 100px;
    flex-shrink: 0;
    overflow: hidden;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    cursor: pointer;
}

.item-photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.photo-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
}

.item-check {
    position: absolute;
    top: 4px;
    left: 4px;
    z-index: 2;
}

.item-body {
    flex: 1;
    padding: 10px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
}

.item-header {
    display: flex;
    align-items: center;
    gap: 6px;
}

.screen-name {
    font-size: 15px;
    font-weight: 500;
    color: #333;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.item-info {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #666;
}

.info-divider {
    color: #ddd;
}

.item-addr {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #969799;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.item-owner {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #666;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* 监播悬浮按钮 */
.float-monitor-btn {
    position: fixed;
    right: 16px;
    bottom: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    background: linear-gradient(135deg, #1989fa 0%, #47a9ff 100%);
    border-radius: 50%;
    box-shadow: 0 4px 16px rgba(25, 137, 250, 0.35);
    cursor: pointer;
    z-index: 1000;
    transition: all 0.3s ease;
}

.sms-dialog-body {
    padding: 16px;
}

.sms-warning {
    color: #f5222d;
    font-size: 14px;
    margin-bottom: 8px;
}

.sms-tip {
    font-size: 14px;
    color: #333;
    margin-bottom: 12px;
}

.sms-input-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.sms-input-row .van-field {
    flex: 1;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
}
</style>
